import React, { useMemo, useRef } from 'react'
import SubHeader from '~/app/components/common/SubHeader/SubHeader'
import { styled } from '~/app/components'
import Section, { SectionBody } from '~/app/components/orders/new/Section'
import { Typography } from '@yanolja-rnd/ya-com-core'
import FixedTooltip from '~/app/components/common/Tooltip/FixedTooltip'
import { toKRW } from '~/app/utils/krNumberToString'
import NolBanner from '~/app/components/Cart/CartPaymentInfo/NolBanner'
import { Icon_help, Icon_error } from '@yanolja-rnd/ya-com-system-icon'

const Container = styled(Section)`
  margin: 12px auto 0;
  max-width: ${({ theme }) => theme.unit.PC_WIDTH}px;
`

const PaymentInfoSectionBody = styled(SectionBody)`
  margin-top: 8px;
  padding-bottom: 20px;
`

const PaymentInfoList = styled.ul`
  border-bottom: 2px dashed ${p => p.theme.color.gray200};
  padding-bottom: 20px;
  margin-bottom: 20px;
`

const PaymentInfoItem = styled.li`
  & > div {
    padding: 8px 0;
    display: flex;
    justify-content: space-between;
  }
`

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
`
const TooltipHelpIcon = styled(Icon_help)`
  cursor: pointer;
  margin-left: 0.2rem;
`

const TimeoutWrapper = styled.div`
  ${Typography('body2.multiline').bold};
  color: ${({ theme }) => theme.color.yaorange};
  display: flex;
  align-items: center;
`

const ErrorIcon = styled(Icon_error)`
  margin-bottom: 0.2rem;
  margin-right: 0.2rem;
  path {
    fill: ${({ theme }) => theme.color.yaorange};
  }
`

const PaymentInfoLabel = styled.h4`
  flex: 1;
  ${Typography('body1').regular};
  color: ${({ theme }) => theme.color.gray600};
`

const PaymentInfoValue = styled.span`
  ${Typography('body1').regular};
  color: ${({ theme }) => theme.color.gray900};
`

const PaymentPriceContainer = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
  justify-content: space-between;

  & > span {
    ${Typography('subtitle1').bold};
  }
`

const PaymentPriceLabel = styled.h3`
  flex: 1;
  ${Typography('body1').regular};
  color: ${({ theme }) => theme.color.gray900};
`

const PaymentInfoDetailItem = styled.li`
  display: flex;
  ${Typography('body2').regular};
  color: ${({ theme }) => theme.color.gray600};
  padding: 8px 0;

  & > h5 {
    flex: 1;
  }

  &:before {
    content: '•';
    margin-right: 5px;
  }
`

const DiscountTooltipTextList = styled.ul`
  list-style: disc;
  padding-left: 1.2rem;
`

interface CartPaymentInfoSectionProps {
  maxDiscountAmount: number
  maxDiscountTypeName: string
  totalPrice: number
  isTimeout: boolean
  isMaxLengthExceeded: boolean
}

const CartPaymentInfoSection: React.FC<CartPaymentInfoSectionProps> = ({
  maxDiscountAmount,
  maxDiscountTypeName,
  totalPrice,
  isTimeout,
  isMaxLengthExceeded,
}) => {
  const priceToolTipContent = useRef(
    <DiscountTooltipTextList>
      <li>최대 할인이 적용된 금액이에요.</li>
      <li>
        실제 결제 금액은 선착순 쿠폰의 잔여 수량 및 기차 묶음할인 금액 변동에 따라 달라질 수 있어요.
      </li>
    </DiscountTooltipTextList>
  ).current

  const amountToPay = useMemo(() => {
    return totalPrice - maxDiscountAmount
  }, [totalPrice, maxDiscountAmount])

  const errorText = useMemo(() => {
    if (isTimeout) {
      return '할인 금액을 불러오지 못했습니다'
    }

    if (isMaxLengthExceeded) {
      return '예약 시 쿠폰할인에서 확인해 주세요'
    }

    return ''
  }, [isTimeout, isMaxLengthExceeded])

  return (
    <Container>
      <SubHeader title={'예약 상품'} hasBottomContent />
      <PaymentInfoSectionBody>
        <PaymentInfoList>
          <PaymentInfoItem>
            <div>
              <PaymentInfoLabel>상품 금액</PaymentInfoLabel>
              <PaymentInfoValue>{toKRW(totalPrice)}</PaymentInfoValue>
            </div>
          </PaymentInfoItem>
          <PaymentInfoItem>
            <div>
              <PaymentInfoLabel>할인 금액</PaymentInfoLabel>
              {!!errorText ? (
                <TimeoutWrapper>
                  <ErrorIcon size={16} />
                  {errorText}
                </TimeoutWrapper>
              ) : (
                <PaymentInfoValue>{toKRW(-maxDiscountAmount)}</PaymentInfoValue>
              )}
            </div>
            {maxDiscountAmount > 0 && (
              <ul>
                <PaymentInfoDetailItem>
                  <h5>{maxDiscountTypeName}</h5>
                  <span>{toKRW(-maxDiscountAmount)}</span>
                </PaymentInfoDetailItem>
              </ul>
            )}
          </PaymentInfoItem>
        </PaymentInfoList>
        <PaymentPriceContainer>
          <FixedTooltip
            content={priceToolTipContent}
            includeCloseIcon={false}
            stickyTo={'left'}
            placement={'top'}
            borderRadius={0.4}
            pointHorizontalOffset={9.2}
            width={26}
          >
            <TooltipContent className="content">
              <PaymentPriceLabel>결제 예상 금액</PaymentPriceLabel>
              <TooltipHelpIcon size={1.5} unit="rem" />
            </TooltipContent>
          </FixedTooltip>
          <span>{toKRW(amountToPay)}</span>
        </PaymentPriceContainer>
        <NolBanner amountToPay={amountToPay} />
      </PaymentInfoSectionBody>
    </Container>
  )
}

export default CartPaymentInfoSection
